import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCurrentVersion, isNewVersion } from '@/lib/changelog-api';
import { useWindowManager } from '@/contexts/WindowManager';
import type { SystemVersion } from '@/types/changelog';
import VersionChangelogWindow from '@/components/windows/VersionChangelogWindow';
import React from 'react';

export function VersionBadge() {
  const [version, setVersion] = useState<SystemVersion | null>(null);
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const windowManager = useWindowManager();

  useEffect(() => {
    const loadVersion = async () => {
      try {
        setError(false);
        const currentVersion = await getCurrentVersion();
        if (currentVersion) {
          setVersion(currentVersion);
          const isNew = await isNewVersion(currentVersion.full_version);
          setHasNewVersion(isNew);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to load version:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadVersion();
  }, []);

  const handleClick = () => {
    if (!windowManager) {
      console.error('Window manager not available');
      return;
    }
    
    console.log('Opening changelog window...');
    
    // Check if window is already open
    if (windowManager.isWindowOpen && windowManager.isWindowOpen('version-changelog')) {
      console.log('Changelog window already open, focusing...');
      windowManager.focusWindow('version-changelog');
      return;
    }
    
    try {
      const ChangelogComponent = React.lazy(() => import('@/components/windows/VersionChangelogWindow'));
      
      windowManager.openWindow(
        'version-changelog',
        'System Changelog',
        <React.Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>}>
          <ChangelogComponent />
        </React.Suspense>,
        {
          position: { x: Math.max(50, window.innerWidth / 2 - 450), y: Math.max(50, window.innerHeight / 2 - 350) },
          size: { width: 900, height: 700 },
          isMinimized: false,
          isMaximized: false,
          appType: 'changelog',
        }
      );
      console.log('Changelog window opened successfully');
    } catch (err) {
      console.error('Failed to open changelog window:', err);
    }
  };

  if (loading) {
    return null; // Even more subtle - nothing while loading
  }

  if (error || !version) {
    return null; // Nothing on error - ultra minimal
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <motion.button
        onClick={handleClick}
        className="group relative cursor-pointer pointer-events-auto focus:outline-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        aria-label="View system changelog"
      >
        {/* Invisible hit area - larger for easier clicking */}
        <div className="absolute inset-0 -m-4" />
        
        {/* Ultra-subtle glow on hover */}
        <div className="absolute inset-0 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute inset-0 bg-gradient-to-tr from-white/[0.02] via-transparent to-white/[0.01] blur-xl" />
        </div>
        
        {/* Main content */}
        <div className="relative flex flex-col items-end gap-0.5">
          {/* Version number - McQueen minimal elegance */}
          <motion.div
            className="text-[11px] font-light tracking-[0.3em] text-white/[0.15] group-hover:text-white/[0.35] transition-all duration-500"
            style={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
            }}
          >
            v{version.full_version}
          </motion.div>
          
          {/* Brand name - barely visible whisper */}
          <motion.div
            className="text-[9px] font-extralight tracking-[0.4em] text-white/[0.08] group-hover:text-white/[0.18] transition-all duration-700"
            style={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              letterSpacing: '0.4em',
              textTransform: 'uppercase',
            }}
          >
            Open Insurance OS
          </motion.div>
          
          {/* NEW indicator - subtle luxury accent */}
          <AnimatePresence>
            {hasNewVersion && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="absolute -top-1 -right-1"
              >
                {/* Ambient glow */}
                <motion.div
                  className="absolute inset-0 bg-white/10 rounded-full blur-md"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                {/* Dot indicator */}
                <div className="relative w-1 h-1 bg-white/40 rounded-full" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Hover tooltip - appears after delay for refinement */}
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 0, y: 5 }}
            whileHover={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="absolute bottom-full right-0 mb-3 pointer-events-none"
          >
            <div className="bg-black/90 backdrop-blur-xl border border-white/[0.08] rounded px-3 py-1.5 shadow-2xl">
              <p className="text-[10px] font-light tracking-wider text-white/70 whitespace-nowrap uppercase">
                View Changelog
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
